import { useSelector, useDispatch } from 'react-redux'
import React, { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { useIsAuthenticated, useMsal, MsalAuthenticationTemplate } from '@azure/msal-react'
import { InteractionType } from '@azure/msal-browser'
import { addAuthorizationHeader } from './utils'
import CircularProgress from '@mui/material/CircularProgress'
import { userLogout, getAppConfig, addCredentials } from './actions'
import { RouterContainer } from './components/RouterContainer'
import { loginRequest } from './authConfig'
import { ErrorPage } from './components/ErrorPage/ErrorPage'
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary'
import { RootState } from './reducers'
import { useIdleTimer } from 'react-idle-timer'

export function Main({ credentials }): JSX.Element | null {
  const isAzureAuthenticated = useIsAuthenticated()
  const { instance, accounts } = useMsal()
  const [isAuthToken, setAuthToken] = useState(false)
  const [isActiveUser, setActiveUser] = useState(false)
  const dispatch = useDispatch()
  const { role, user, name, authErrorMessage, isAppConfigSuccessful } = useSelector(
    (state: RootState) => state.userLogin,
  )
  const handleLogout = () => {
    dispatch(userLogout())
    localStorage.clear()
    instance.logoutRedirect({
      account: accounts[0],
      postLogoutRedirectUri: '/',
    })
  }

  useEffect(() => {
    setActiveUser(role !== '' && isAzureAuthenticated && isAppConfigSuccessful && isAuthToken)
  }, [role, isAzureAuthenticated, isAppConfigSuccessful, isAuthToken])

  useEffect(() => {
    const accounts = instance.getAllAccounts()
    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0])
    }
    const account = instance.getActiveAccount()
    if (account) {
      instance
        .acquireTokenSilent({
          ...loginRequest,
          account: account,
        })
        .then(async (response) => {
          addAuthorizationHeader({ token: response?.idToken })
          await dispatch(getAppConfig({ userLoginId: account?.username, name: account?.name }))
          await dispatch(addCredentials(credentials))
          setAuthToken(true)
        })
    }
  }, [accounts, instance, isAzureAuthenticated, dispatch])

  const idleTimer = useIdleTimer({ onIdle: handleLogout, timeout: 12 * 60 * 1000 })

  function ErrorComponent({ error }) {
    return <p>An Error Occurred: {error}</p>
  }

  function LoadingComponent() {
    return (
      <Box sx={{ display: 'flex', marginLeft: '105vh', marginTop: '50vh', color: '#0a688d' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (authErrorMessage === 'Invalid User') {
    return <ErrorPage handleLogout={handleLogout} message="Your account is not active" />
  } else if (authErrorMessage !== '') {
    return <ErrorPage handleLogout={handleLogout} message={authErrorMessage} />
  }

  return (
    <ErrorBoundary handleLogout={handleLogout}>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={loginRequest}
        errorComponent={ErrorComponent}
        loadingComponent={LoadingComponent}
      >
        {isActiveUser ? <RouterContainer handleLogout={handleLogout} name={name} role={role} user={user} /> : null}
      </MsalAuthenticationTemplate>
    </ErrorBoundary>
  )
}
